/**
 * ChainTerminusButton.tsx - Floating + button for adding steps at chain endpoints
 *
 * Renders a circular FAB at the end of each branch/chain in the automation flow.
 * When clicked, opens a modal to select the step type to add.
 * Also supports visual highlighting during drag operations.
 *
 * Related:
 *   - hooks/useChainTermini.ts - Identifies where to render these buttons
 *   - components/StepPaletteModal.tsx - Step type selection modal
 */

import { css } from 'aphrodite';
import { useCallback, useState, useRef } from 'react';
import { StepPaletteModal } from './StepPaletteModal';
import type { ChainTerminus } from '../hooks/useChainTermini';
import { NODE_TYPES } from '../types';
import { MAX_BRANCH_NESTING_DEPTH } from '../utils/nestingLimits';
import { styleSheet } from './styles/ChainTerminusButtonStyles';

interface ChainTerminusButtonProps {
	/** Information about the chain terminus */
	terminus: ChainTerminus;
	/** Callback when a step type is selected */
	onAddStep: (terminus: ChainTerminus, stepType: string) => Promise<void>;
	/** Whether the button is disabled */
	disabled?: boolean;
	/** Whether this button is highlighted as the closest drop target during drag */
	isHighlighted?: boolean;
	/** Step types to exclude from the palette modal */
	excludedStepTypes?: string[];
}

export function ChainTerminusButton({
	terminus,
	onAddStep,
	disabled = false,
	isHighlighted = false,
	excludedStepTypes,
}: ChainTerminusButtonProps) {
	const [isOpen, setIsOpen] = useState(false);
	const [isAdding, setIsAdding] = useState(false);
	const buttonRef = useRef<HTMLButtonElement>(null);

	const handleClick = useCallback(() => {
		if (disabled || isAdding) {
			return;
		}
		setIsOpen(true);
	}, [disabled, isAdding]);

	const handleSelectStepType = useCallback(
		async (stepType: string) => {
			if (isAdding) {
				return;
			}

			setIsAdding(true);
			setIsOpen(false);

			try {
				await onAddStep(terminus, stepType);
			} catch (error) {
				console.error('[ChainTerminusButton] Error in onAddStep:', error);
			} finally {
				setIsAdding(false);
			}
		},
		[terminus, onAddStep, isAdding]
	);

	const handleClose = useCallback(() => {
		setIsOpen(false);
	}, []);

	const derivedExcludedStepTypes =
		terminus.nestingDepth >= MAX_BRANCH_NESTING_DEPTH
			? Array.from(new Set([...(excludedStepTypes || []), NODE_TYPES.SWITCH_STEP]))
			: excludedStepTypes;

	// Note: Modal handles its own close behavior (ESC key, backdrop click)

	return (
		<div
			className={css(styleSheet.container)}
			style={{
				position: 'absolute',
				left: terminus.position.x,
				top: terminus.position.y,
				transform: 'translate(-50%, 0)',
				pointerEvents: 'all',
			}}
		>
			<button
				ref={buttonRef}
				type='button'
				className={css(
					styleSheet.fab,
					isAdding && styleSheet.fabLoading,
					isOpen && styleSheet.fabActive,
					isHighlighted && styleSheet.fabHighlighted
				)}
				onClick={handleClick}
				disabled={disabled || isAdding}
				aria-label='Add step to automation'
				aria-expanded={isOpen}
				aria-haspopup='true'
			>
				{isAdding ? (
					<svg className={css(styleSheet.spinner)} viewBox='0 0 24 24' fill='none' stroke='currentColor'>
						<circle cx='12' cy='12' r='10' strokeWidth='2' strokeLinecap='round' strokeDasharray='60' />
					</svg>
				) : (
					<svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5'>
						<line x1='12' y1='5' x2='12' y2='19' strokeLinecap='round' />
						<line x1='5' y1='12' x2='19' y2='12' strokeLinecap='round' />
					</svg>
				)}
			</button>
			<div className={css(styleSheet.tooltip)}>Add Step</div>
			{isOpen && (
				<StepPaletteModal
					isOpen={isOpen}
					onClose={handleClose}
					onSelectStep={handleSelectStepType}
					excludedStepTypes={derivedExcludedStepTypes}
				/>
			)}
		</div>
	);
}
